import * as PIXI from 'pixi.js';
import { int2rgbArray } from '../../libs/videoData';
import { createGlyphRun } from './fontkitUtils';
import type { WordElementMetrics } from './WordElementMetrics';
import { isNewline } from './textMetricsHelpers';


/**
 * A WordElement will render a single element of text. It uses fontkit to
 * render the text into a canvas and then creates a texture from the canvas.
 *
 * @see [PIXI.Text]{@link https://pixijs.download/dev/docs/PIXI.Text.html}
 */
export class WordElementTexture extends PIXI.Text {
    #metrics: WordElementMetrics = null;
    #referenceToOutputMultiplier: number;

    localStyle: any;

    /**
     * @param metrics - word element metrics to use
     */
    constructor(metrics: WordElementMetrics, referenceToOutputMultiplier: number = 1) {
        super(metrics.text, metrics.style);
        this.#metrics = metrics;
        this.#referenceToOutputMultiplier = referenceToOutputMultiplier;
    }

    get metrics(): WordElementMetrics {
        return this.#metrics;
    }

    /**
     * Indicates if the WordElement contains text visible to the user (for example not
     * spaces or new lines)
     */
    get hasVisibleText(): boolean {
        const bounds = this.#metrics.getBounds();
        return bounds.width > 0;
    }

    /**
     * Renders text and updates it when needed.
     *
     * @param respectDirty - Whether to abort updating the text if the Text isn't dirty
     * and the function is called.
     */
    override updateText(respectDirty?: boolean) {
        const style = this.#metrics.style;
        const context = this.context;

        // check if style has changed.
        if (!this.localStyle?.equals(style)) {
            this.dirty = true;
            this.localStyle = style;
        }

        if (!this.dirty && respectDirty) {
            return;
        }

        if (isNewline(this.text) || PIXI.TextMetrics.isBreakingSpace(this.text)) {
            // Nothing to draw
            // Note to create a webgl texture from a canvas it needs at least 1 px
            this.canvas.width = 1;
            this.canvas.height = 1;
            this.#updateTexture();
            return;
        }

        const bounds = this.#metrics.getBounds();
        this.canvas.width = Math.max(Math.ceil(bounds.width * this.resolution * this.#referenceToOutputMultiplier), 1);
        this.canvas.height = Math.max(Math.ceil(bounds.height * this.resolution * this.#referenceToOutputMultiplier), 1);

        // Is not exactly equal to the resolution because of the ceiling operation, so compute it
        // context.scale(1, -1);
        const scaleX = this.canvas.width / bounds.width;
        const scaleY = this.canvas.height / bounds.height;
        // The canvas positive y-direction is opposite of the fontkit text grid's positive
        // y-dir, so also flip the y-axis
        context.scale(scaleX, -scaleY);

        // Clear anything in the drawing area
        context.clearRect(0, 0, this.canvas.width, this.canvas.height);

        // The canvas has its origin in the top left corner, but the fontkit text origin is at
        // the advance width origin of the first glyph, on its baseline. So we need to
        // translate the path to start at the text origin (instead of the canvas origin).
        // At this point the canvas y-axis has been flipped so x and y directions are the same
        // for the canvas and for the text grid
        const offsetX = -this.#metrics.bearingX;
        const offsetY = -this.#metrics.bearingY;
        context.translate(offsetX, offsetY);

        // First draw the drop shadow
        if (style.dropShadow) {
            this._draw(true);
        }
        // Now draw the characters
        this._draw(false);

        this.#updateTexture();
    }

    /**
     * Applies the styles and draws to the canvas.
     * This happens in two passes (an initial one for the shadow) because we're drawing
     * character by character. If we drew the character and its shadow at the same time and then
     * the next character, it means a previous character could be overlapped by another
     * character's shadow. So this should be called with the shadow pass first, so that shadows
     * don't overlap characters.
     *
     * @internal
     * @param isShadowPass - indicates if drawing the shadow layer
     */
    _draw(isShadowPass: boolean) {
        const style = this.#metrics.style;
        const context = this.context as CanvasRenderingContext2D;
        let x = 0;
        let y = 0;

        // Always use the same fill color so the shadow pass doesn't bleed another color through
        // since the pixels aren't always perfectly aligned between the two passes
        const rgbFill = int2rgbArray(style.fill);
        context.fillStyle = `rgb(${rgbFill[0]},${rgbFill[1]},${rgbFill[2]})`;

        if (isShadowPass) {
            // Don't stroke the shadow
            context.strokeStyle = 'transparent';
            context.lineWidth = 0;

            const rgb = int2rgbArray(style.dropShadowColor);
            context.shadowColor = `rgba(${rgb[0]},${rgb[1]},${rgb[2]},${style.dropShadowAlpha})`;
            context.shadowBlur = style.dropShadowBlur * this.#referenceToOutputMultiplier;
            // When scaling a canvas the shadow distances don't get adjusted, so we manually
            // handle that here
            context.shadowOffsetX = style.dropShadowDistanceX * this.#referenceToOutputMultiplier;
            context.shadowOffsetY = style.dropShadowDistanceY * this.#referenceToOutputMultiplier;
        }
        else {
            context.shadowColor = 'rgba(0, 0, 0, 0)';
            context.shadowBlur = 0;
            context.shadowOffsetX = 0;
            context.shadowOffsetY = 0;

            // apply the stroke if it's not the shadow pass (we don't want a stroke on the shadow)
            if (style.strokeThickness > 0) {
                const rgbStroke = int2rgbArray(style.stroke);
                context.strokeStyle = `rgb(${rgbStroke[0]},${rgbStroke[1]},${rgbStroke[2]})`;
                context.lineWidth = style.strokeThickness;
            }
        }

        const scale = style.fontSize / this.#metrics.font.unitsPerEm;
        const run = createGlyphRun(this.#metrics.text, this.#metrics.font);
        run.glyphs.forEach((glyph, index) => {
            const pos = run.positions[index];
            context.save();
            context.translate((x + pos.xOffset) * scale, (y + pos.yOffset) * scale);
            context.beginPath();
            glyph.render(context, style.fontSize);

            if (!isShadowPass && style.strokeThickness > 0) {
                context.stroke();
            }

            context.restore();
            x += pos.xAdvance;
            y += pos.yAdvance;
        });
    }

    /**
     * Updates texture size based on canvas size.
     *
     * The reason we override this method is so that we can accurately size the texture.
     * The issue with PIXI's implementation is that they divide the scaled canvas by
     * the resolution and then round it up again for a whole number. However, we already
     * know the exact size of the canvas so let's just use that to prevent introducing
     * size discrepancies further down the line.
     */
    #updateTexture(): void {
        const canvas = this.canvas;
        const texture = this._texture;
        const baseTexture = texture.baseTexture;

        const bounds = this.#metrics.getBounds();
        texture.trim.width = texture._frame.width = bounds.width;
        texture.trim.height = texture._frame.height = bounds.height;

        texture.orig.width = texture._frame.width;
        texture.orig.height = texture._frame.height;

        // call sprite onTextureUpdate to update scale if _width or _height were set
        this._onTextureUpdate();

        baseTexture.setRealSize(canvas.width, canvas.height, this._resolution * this.#referenceToOutputMultiplier);

        this.dirty = false;
    }
}
